import { ReloadOutlined } from '@ant-design/icons'
import Button from '@components/Button'
import Input from '@components/Input'
import * as Sentry from '@sentry/browser'
import { NetWorkService, validResponse } from '@utils/API'
import { CODE_SUCCESS } from '@utils/constan'
import { message } from 'antd'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { RootState } from 'store'
import { Type_Initstate } from 'store/appSlice'
import { BillsHistory } from '../../../types/general'

const CAPTCHA_TTL_MS = 5 * 60 * 1000

interface IProps {
  handleGetBillList: (maSv: string, cccd: string, captcha?: { id: string; text: string }) => Promise<boolean>
  isHistory?: boolean
  bills?: BillsHistory[]
}

type IFrom = {
  maSV: string
  cccd?: string
}

const Search: FC<IProps> = ({ handleGetBillList, isHistory }: IProps) => {
  const params = useParams()
  const { edubillDetail } = useSelector<RootState, Type_Initstate>((state) => state.app)
  const [valueSearch, setValueSearch] = useState<IFrom | undefined>(undefined)
  const [isSearchCCCD, setIsSearchCCCD] = useState<boolean>(true)
  const [captcha, setCaptcha] = useState<{ id: string; svg: string } | null>(null)
  const [captchaText, setCaptchaText] = useState('')
  const [captchaGeneratedAt, setCaptchaGeneratedAt] = useState(0)
  const submittingRef = useRef(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { Get } = NetWorkService

  const handleSetValue = (value: string | null, key: string) => {
    setValueSearch({ ...valueSearch, [`${key}`]: value.trim() })
  }

  const fetchCaptcha = useCallback(async () => {
    if (!params.orgCode || !params.schoolCode) return

    try {
      const res: any = await Get<any>({
        url: `/api/organization/${params.orgCode}/school/${params.schoolCode}/captcha-public`,
      })
      if (res && validResponse(res) && res.code === CODE_SUCCESS) {
        const data = res?.data?.data
        if (data?.enabled && data?.id && data?.svg) {
          setCaptcha({ id: data.id, svg: data.svg })
          setCaptchaText('')
          setCaptchaGeneratedAt(Date.now())
        } else {
          setCaptcha(null)
          setCaptchaText('')
          setCaptchaGeneratedAt(0)
        }
      }
    } catch (error) {
      setCaptcha(null)
      setCaptchaText('')
      setCaptchaGeneratedAt(0)
      Sentry.captureException(error)
    }
  }, [Get, params.orgCode, params.schoolCode])

  const getData = async () => {
    try {
      const res: any = await Get<any>({
        url: `/api/organization/${edubillDetail.orgCode}/school/${edubillDetail.schoolCode}/edubill/detail`,
      })
      if (res && validResponse(res) && res.code === CODE_SUCCESS) {
        const response = res?.data.data
        setIsSearchCCCD(response?.isSearchCCCD)
      } else {
        res && validResponse(res) ? message.error(res.data.data.message) : message.error('Lấy dữ liệu lỗi')
        Sentry.captureMessage('Lấy dữ liệu lỗi')
      }
    } catch (error) {
      message.error('Có lỗi xảy ra')
      console.log(error)
      Sentry.captureException(error)
    }
  }

  useEffect(() => {
    if (edubillDetail?.orgCode && edubillDetail?.schoolCode) {
      getData()
    }
  }, [edubillDetail?.orgCode, edubillDetail?.schoolCode])

  useEffect(() => {
    if (params.orgCode && params.schoolCode) {
      fetchCaptcha()
    }
  }, [params.orgCode, params.schoolCode, fetchCaptcha])

  const handleSubmit = async () => {
    if (submittingRef.current) return
    const maSv = valueSearch?.maSV
    const cccd = isSearchCCCD ? valueSearch?.cccd : '0'

    if (!maSv || (isSearchCCCD && !cccd) || (captcha && !captchaText.trim())) return

    submittingRef.current = true
    setIsSubmitting(true)
    try {
      if (!captcha) {
        await handleGetBillList(maSv, cccd)
        return
      }

      if (Date.now() - captchaGeneratedAt >= CAPTCHA_TTL_MS) {
        message.warning('Mã xác thực đã hết hạn, vui lòng nhập mã mới')
        await fetchCaptcha()
        return
      }

      const ok = await handleGetBillList(maSv, cccd, {
        id: captcha.id,
        text: captchaText.trim(),
      })

      if (!ok) {
        await fetchCaptcha()
      }
    } finally {
      submittingRef.current = false
      setIsSubmitting(false)
    }
  }

  const isSubmitDisabled =
    !valueSearch?.maSV || (isSearchCCCD && !valueSearch?.cccd) || (!!captcha && !captchaText.trim()) || isSubmitting
  const isStudentCodeOnly = !isSearchCCCD && !captcha

  return (
    <div
      className={`relative z-10 mx-auto w-full max-w-[1040px] px-2 sm:px-4 ${isHistory ? 'mt-4 md:-mt-10' : 'mt-4'}`}
    >
      <form
        className="p-4 bg-white flex md:justify-between flex-col rounded-[16px] w-full"
        style={{
          boxShadow: '0px 4px 54px rgba(0, 0, 0, 0.1)',
        }}
        onSubmit={(event) => {
          event.preventDefault()
          if (!isSubmitDisabled) void handleSubmit()
        }}
      >
        <div className="w-full ">
          <div className={`flex md:items-center flex-col md:flex-row ${isStudentCodeOnly ? '' : 'mb-3'}`}>
            <p className="text-[#595959] text-[16px] pr-2 md:w-[180px] font-[400]">Mã học viên</p>
            <div className={`w-full min-w-0 ${isStudentCodeOnly ? 'flex flex-col gap-2 md:flex-row md:items-center' : ''}`}>
              <div className="min-w-0 flex-1">
                <Input onChange={(e) => handleSetValue(e, 'maSV')} placeholder="Nhập mã học viên" />
              </div>
              {isStudentCodeOnly && (
                <Button
                  text="Tra cứu học phí"
                  htmlType="submit"
                  disabled={isSubmitDisabled}
                  width="!w-full md:!w-[170px] shrink-0"
                />
              )}
            </div>
          </div>
          {isSearchCCCD && (
            <div className="flex md:items-center mb-3 flex-col md:flex-row">
              <p className="text-[#595959] text-[16px] pr-2 md:w-[180px] font-[400]">Căn cước công dân</p>
              <Input onChange={(e) => handleSetValue(e, 'cccd')} placeholder="Nhập CCCD" />
            </div>
          )}
          {captcha && (
            <>
              <div className="flex md:items-center mb-3 flex-col md:flex-row">
                <p className="text-[#595959] text-[16px] pr-2 md:w-[180px] font-[400]">Mã xác thực</p>
                <Input value={captchaText} onChange={setCaptchaText} placeholder="Nhập mã xác thực" />
              </div>
              <div className="flex md:items-center mb-3 flex-col md:flex-row">
                <p className="hidden md:block md:w-[180px] shrink-0" aria-hidden="true" />
                <div className="flex items-center gap-3 w-full md:flex-1">
                  <img
                    src={`data:image/svg+xml;utf8,${encodeURIComponent(captcha.svg)}`}
                    alt="captcha"
                    className="h-[43px] md:h-[51px] shrink-0"
                  />
                  <ReloadOutlined
                    className="cursor-pointer text-[#045993] text-[20px] shrink-0"
                    onClick={fetchCaptcha}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        {!isStudentCodeOnly && (
          <div className="flex justify-center">
            <Button text="Tra cứu học phí" htmlType="submit" disabled={isSubmitDisabled} width="w-full md:w-[170px]" />
          </div>
        )}
      </form>
    </div>
  )
}

export default Search
